import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, ShieldCheck, User, GraduationCap, Loader2, Copy, ExternalLink } from "lucide-react";
import { useTenant } from "../../context/TenantContext";
import { TenantLogo } from "../../components/TenantBranding";
import { getApiUrl } from "../../config/api";
import "../../styles/ForgotPassword.css";

const ForgotPassword: React.FC = () => {
  const { branding } = useTenant();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<'student' | 'teacher'>('student');
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [isLoading, setIsLoading] = useState(false);
  const [recoveryLink, setRecoveryLink] = useState("");
  const [recoveryError, setRecoveryError] = useState("");

  useEffect(() => {
    document.title = `Forgot Password | ${branding.displayName}`;
  }, [branding.displayName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    setMessageType('success');
    setRecoveryLink("");
    setRecoveryError("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setMessage("Invalid identity detected. Please provide a valid email uplink.");
      setMessageType('error');
      return;
    }

    setIsLoading(true);
    try {
      const payload = { email: email.trim().toLowerCase(), role };
      const res = await axios.post(getApiUrl("auth/forgot-password"), payload);

      setMessage(res.data.message || "Reset protocol initiated. If an account is registered with this email, a security override link has been dispatched to your inbox.");
      setMessageType('success');
      setRecoveryLink(res.data?.recovery?.resetLink || "");
      setRecoveryError(res.data?.recovery?.deliveryError || "");
      setEmail("");
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || "Uplink error during reset request. Please check your connection and try again.";
      setMessage(errorMsg);
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="brand-soft-bg absolute top-0 right-0 w-[500px] h-[500px] rounded-full -mr-64 -mt-64 blur-3xl"></div>
      <div className="brand-soft-bg absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full -ml-64 -mb-64 blur-3xl"></div>

      <div className="flex-grow flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden transform transition-all duration-500 hover:shadow-teal-500/10">
          <div className="brand-surface-accent h-2" />

          <div className="p-10">
            <div className="flex justify-center mb-8">
              <TenantLogo size="lg" showText />
            </div>

            <div className="text-center mb-10">
              <h2 className="text-3xl font-black text-slate-800 tracking-tight mb-3">
                Security Recovery
              </h2>
              <p className="text-sm font-medium text-slate-400 max-w-xs mx-auto leading-relaxed">
                Initiate the master credential override protocol to regain access to your terminal.
              </p>
            </div>

            {message && (
              <div className={`mb-8 p-4 rounded-2xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 ${messageType === 'success' ? 'brand-soft-bg border border-slate-200' : 'bg-red-50 text-red-700 border border-red-100'
                }`}>
                {messageType === 'success'
                  ? <ShieldCheck className="shrink-0 mt-0.5" size={18} style={{ color: branding.primaryColor }} />
                  : <Mail className="shrink-0 mt-0.5" size={18} />}
                <p className="text-xs font-bold leading-relaxed">{message}</p>
              </div>
            )}

            {recoveryLink && (
              <div className="mb-8 p-4 rounded-2xl bg-amber-50 text-amber-800 border border-amber-100 space-y-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="shrink-0 mt-0.5" size={18} />
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest">In-System Recovery Ready</p>
                    <p className="text-xs font-bold leading-relaxed mt-1">
                      Email delivery failed in this environment, so a local recovery link was generated for testing.
                    </p>
                    {recoveryError && (
                      <p className="text-[11px] font-medium text-amber-700 mt-2 break-words">
                        Delivery issue: {recoveryError}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => window.location.assign(recoveryLink)}
                    className="brand-button-primary flex-1 font-black py-3 rounded-2xl shadow-lg flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.18em]"
                  >
                    <ExternalLink size={14} />
                    Continue In System
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await navigator.clipboard.writeText(recoveryLink);
                      setMessage("Local recovery link copied.");
                      setMessageType("success");
                    }}
                    className="utopia-btn-secondary flex-1 font-black py-3 rounded-2xl flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.18em]"
                  >
                    <Copy size={14} />
                    Copy Recovery Link
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Identity Uplink (Email)</label>
                <div className="relative group">
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 brand-link transition-colors">
                      <Mail size={18} />
                    </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="name@school.edu"
                    className="brand-input-focus w-full pl-14 pr-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-bold text-slate-700 outline-none focus:bg-white transition-all"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Terminal Role</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setRole('student')}
                    className={`flex items-center justify-center gap-2 p-4 rounded-2xl border-2 transition-all ${role === 'student'
                        ? 'brand-soft-bg border-slate-300'
                        : 'bg-slate-50 border-slate-50 text-slate-400 hover:border-slate-200'
                      }`}
                    style={role === 'student' ? { color: branding.primaryColor } : undefined}
                  >
                    <GraduationCap size={16} />
                    <span className="text-[10px] font-black uppercase tracking-tight">Student</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole('teacher')}
                    className={`flex items-center justify-center gap-2 p-4 rounded-2xl border-2 transition-all ${role === 'teacher'
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'bg-slate-50 border-slate-50 text-slate-400 hover:border-slate-200'
                      }`}
                  >
                    <User size={16} />
                    <span className="text-[10px] font-black uppercase tracking-tight">Teacher</span>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="brand-button-primary w-full font-black py-4 rounded-2xl shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.2em]"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <>
                    <span>Dispatch Link</span>
                    <ArrowLeft className="rotate-180" size={14} />
                  </>
                )}
              </button>
            </form>

            <div className="mt-10 pt-8 border-t border-slate-50 transition-all">
                <Link
                to="/signin"
                className="brand-link flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors"
              >
                <ArrowLeft size={14} />
                Back to Authentication
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;








